<script setup lang="ts">
import FileImage from '@/core/files/FileImage.vue';
import ExternalMaterialIcon from '@/assets/icons/ExternalMaterialIcon.vue';
import ClickableListItem from '@/core/elements/ClickableListItem.vue';
import CustomCheckbox from '@/core/elements/CustomCheckbox.vue';
import InfoTooltip from '@/core/elements/InfoTooltip.vue';
import SearchTextHighlighter from '@/core/elements/SearchTextHighlighter.vue';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { useSchoolLevels } from '@/composables/useSchoolLevels';
import { EPosition } from 'common/enum/misc.enum';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

export interface IExternalMaterialListItem {
  name: string;
  subjectSource: string | null;
  logoUrl: string | null;
  logoId: number | null;
  sourceUrl: string | null;
  description: string | null;
  schoolLevels: number[];
  isFree: boolean;
  licenseInfo: string | null;
  premium?: boolean;
  isOfficial?: boolean;
}

const props = withDefaults(
  defineProps<{
    material: IExternalMaterialListItem;
    selectable?: boolean;
    selected?: boolean;
  }>(),
  { selectable: false, selected: false },
);

const emit = defineEmits<{ click: [] }>();

const { t } = useI18n();

const { currentTenant } = useCurrentTenant();
const { getSchoolLevelLabel } = useSchoolLevels(
  () => currentTenant.value?.schoolLevels ?? [],
  () => currentTenant.value?.schoolLevelLabelMode,
);
const schoolLevelsLabel = (): string => props.material.schoolLevels.map(getSchoolLevelLabel).join(', ');

const costLabel = (): string => {
  if (props.material.licenseInfo?.trim()) return props.material.licenseInfo;
  if (props.material.isFree) return t('supportMeasureEntry.materials.free');
  return t('supportMeasureEntry.materials.paid');
};

const rowComponent = computed(() => (props.selectable ? ClickableListItem : 'li'));
const rowClass = computed(() =>
  props.selectable
    ? [
        'h-cell !gap-md !rounded-md !px-md !py-smd md:h-auto md:min-h-16 md:!gap-sm md:!rounded-sm md:!p-md',
        props.selected ? 'bg-tint/10' : '',
      ]
    : ['flex items-center gap-sm rounded-sm p-sm'],
);
const logoClass = computed(() => (props.selectable ? 'size-12 md:mr-sm' : 'size-9'));
const isCustom = computed(() => props.material.isOfficial === false);
</script>

<template>
  <component :is="rowComponent" :class="rowClass" @click="selectable ? emit('click') : undefined">
    <CustomCheckbox v-if="selectable" :model-value="selected" class="pointer-events-none md:mr-sm" />

    <FileImage
      v-if="material.logoId || material.logoUrl"
      :source="material.logoId ?? material.logoUrl"
      alt=""
      :class="['shrink-0 rounded-lg border border-surface-secondary bg-white object-contain', logoClass]"
    />
    <span
      v-else
      :class="[
        'flex shrink-0 items-center justify-center rounded-lg border border-surface-secondary bg-white text-secondary',
        logoClass,
      ]"
    >
      <ExternalMaterialIcon :class="isCustom ? 'size-4 text-black' : 'size-icon-md'" />
    </span>

    <div class="min-w-0 flex-1">
      <span class="block truncate pl-0.5 pr-0.5 text-body" :class="selected ? 'font-semibold text-tint' : ''">
        <template v-if="isCustom">{{ material.name }}</template>
        <SearchTextHighlighter v-else :text="material.name" />
      </span>
      <span v-if="material.premium" class="block truncate pl-0.5 pr-0.5 text-body text-tint">
        {{ $t('externalMaterial.premiumPartner') }}
      </span>
    </div>

    <InfoTooltip
      :position="EPosition.BOTTOM_RIGHT"
      content-style="w-[300px]"
      icon-style="size-icon-sm"
      class="shrink-0"
      @click.stop
    >
      <div class="flex flex-col gap-sm text-left text-base text-white">
        <div>
          <div class="font-semibold">{{ material.name }}</div>
          <div v-if="!isCustom && material.subjectSource" class="text-white">{{ material.subjectSource }}</div>
        </div>

        <!-- a custom app only ever carries its name; cost/levels/etc. are placeholder defaults, so show name only -->
        <template v-if="!isCustom">
          <p v-if="material.description" class="whitespace-pre-line text-white">{{ material.description }}</p>

          <div class="flex flex-col gap-xs">
            <div v-if="material.schoolLevels.length">
              <span class="text-white">{{ $t('externalMaterial.schoolLevels') }}: </span>{{ schoolLevelsLabel() }}
            </div>
            <div>
              <span class="text-white">{{ $t('supportMeasureEntry.materials.cost') }}: </span>{{ costLabel() }}
            </div>
          </div>

          <a
            v-if="material.sourceUrl"
            :href="material.sourceUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="font-semibold text-white underline underline-offset-2 hover:text-white/80"
          >
            {{ $t('supportMeasureEntry.materials.visitWebsite') }} ↗
          </a>
        </template>
      </div>
    </InfoTooltip>
  </component>
</template>
